/* eslint-disable max-params */
import type { DSComboboxT } from '../react-desc-prop-types.js';
import { MENU_OPTION_TYPES } from '../constants.js';
export const getSelectableOptions = (options: Array<DSComboboxT.OptionTypes>): Array<DSComboboxT.OptionTypes> =>
  options.filter((option) => option.type === MENU_OPTION_TYPES.OPTION && !option.disabled);

export const getOptions = (options: Array<DSComboboxT.OptionTypes>): Array<DSComboboxT.OptionTypes> =>
  options.filter((option) => option.type === MENU_OPTION_TYPES.OPTION);

export const isSelectedValueEmpty = (value: DSComboboxT.SelectedOptionsT): boolean =>
  Array.isArray(value) ? value.length === 0 : !value;

export const isSelectedValueMultiple = (value: DSComboboxT.SelectedOptionsT): boolean => Array.isArray(value);

export const isSelected = (value: DSComboboxT.SelectedOptionsT, activeOption: DSComboboxT.ItemOption): boolean => {
  if (isSelectedValueMultiple(value)) {
    const multiValue = value as DSComboboxT.ItemOption[];
    return multiValue.some((item) => item?.value === activeOption?.value);
  }
  const singleValue = value as DSComboboxT.ItemOption;
  return singleValue?.value === activeOption.value;
};

export function isArrayOfItems(value: DSComboboxT.SelectedOptionsT): value is DSComboboxT.ItemOption[] {
  return Array.isArray(value) && value.length > 0;
}

export const isItem = (value: DSComboboxT.SelectedOptionsT): value is DSComboboxT.ItemOption =>
  !Array.isArray(value) && value !== null && typeof value === 'object';

export const findInCircularList = (
  list: DSComboboxT.OptionTypes[],
  from: number,
  criteria: (item: DSComboboxT.OptionTypes) => boolean,
  step = 1,
): number => {
  if (list.length === 0) return from;

  let i = (from + step + list.length) % list.length;
  let iterations = 0;

  while (iterations < list.length) {
    if (criteria(list[i])) return i;

    i = (i + step + list.length) % list.length;
    iterations += 1;
  }

  return from; // Return the original index if no match is found
};
export const getLastValueSelected = (selectedValues: DSComboboxT.SelectedOptionsT) => {
  if (Array.isArray(selectedValues)) {
    return selectedValues[selectedValues.length - 1];
  }
  return selectedValues;
};

export const getFirstOption = (
  options: DSComboboxT.OptionTypes[],
  selectedValues?: DSComboboxT.SelectedOptionsT,
): string => {
  if (selectedValues) {
    const lastValue = getLastValueSelected(selectedValues);
    if (lastValue) return lastValue.dsId;
  }
  for (let i = 0; i < options.length; i += 1)
    if (!['section', 'separator'].includes(options[i].type) && !options[i].disabled) {
      return options[i].dsId;
    }
  // list of disabled item results return ''
  return '';
};

export const getSuggestedValueOnChange = (
  selectedOption: DSComboboxT.ItemOption,
  selectedValues: DSComboboxT.SelectedOptionsT,
  isNonClearable?: boolean,
) => {
  if (Array.isArray(selectedValues)) {
    const isFound = selectedValues.find((item) => item.type === 'option' && item.value === selectedOption.value);
    if (isFound) {
      if (isNonClearable) return selectedValues;
      return selectedValues.filter((item) => item.type === 'option' && item.value !== selectedOption.value);
    }
    return [...selectedValues, selectedOption];
  }
  if (selectedValues && selectedValues.dsId === selectedOption.dsId) {
    if (isNonClearable) return selectedOption;
    return null;
  }
  return selectedOption;
};

export const filterOptions = (inputValue: string, options: DSComboboxT.OptionTypes[]) => {
  if (!inputValue) return options;
  return options.filter(
    (option) =>
      option.type === MENU_OPTION_TYPES.OPTION && option.label.toLowerCase().includes(inputValue.toLowerCase()),
  );
};

export const getFilteredOptionsSelected = (
  filteredOption: DSComboboxT.OptionTypes[],
  selectedValues: DSComboboxT.ItemOption[],
) => filteredOption.filter((option) => option.type === MENU_OPTION_TYPES.OPTION && isSelected(selectedValues, option));

export const scrollIfNeeded = (
  elem: HTMLElement,
  options = { behavior: 'smooth', block: 'end', inline: 'nearest' } as ScrollIntoViewOptions | undefined,
) => {
  const bounding = elem.getBoundingClientRect();
  if (
    !(
      bounding.top >= 0 &&
      bounding.left >= 0 &&
      bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  )
    elem.scrollIntoView(options);
};
